import { observable, action, makeObservable, computed } from "mobx";
import { Config } from "src/config/config";
import { Customer, BackButton, User } from "src/types";
import { CUSTOMER_FEATURE, CUSTOMER_FEATURE_FLAGS, CUSTOMER_OPTION } from "src/api/customer";
import { History } from "history";
import { RootStore } from "./RootStore";

export interface ListParameters {
    searchQuery?: string;
    scrollPosition?: number;
    paginationPage?: number;
    filterValue?: string;
    sortValue?: string;
}

const HISTORY_STACK_MAX_SIZE = 20;

export class NavigationStore {
    rootStore: RootStore;

    customer: Customer = {
        name: "",
        userName: "",
        email: "",
        options: [],
        features: [],
        type: 0,
        featureflags: [],
        erpcoud: false,
        subdomain: ""
    };
    user: User = {};
    closeNavTarget = "";
    currentTitle = "";
    appheaderTitle = "";
    currentSubtitle: string | null = null;
    singleRealestate: boolean = false;
    isCondominium: boolean = false;
    isConsolidation: boolean = false;
    currentOwnerId: string | null = null;
    backButton: BackButton | null = null;
    actionButtonLeft: React.ReactNode | null = null;
    actionButtonMiddle: React.ReactNode | null = null;
    actionButtonRight: React.ReactNode | null = null;
    listMap: Map<string, ListParameters> = new Map();
    historyStack: string[] = [];
    consolidationId: string = "";
    history: History | null = null;

    constructor(rootStore: RootStore) {
        this.rootStore = rootStore;
        makeObservable(this, {
            customer: observable,
            user: observable,
            closeNavTarget: observable,
            currentTitle: observable,
            appheaderTitle: observable,
            currentSubtitle: observable,
            singleRealestate: observable,
            isCondominium: observable,
            isConsolidation: observable,
            currentOwnerId: observable,
            backButton: observable,
            actionButtonLeft: observable.ref,
            actionButtonMiddle: observable.ref,
            actionButtonRight: observable.ref,
            listMap: observable,
            historyStack: observable,
            consolidationId: observable,
            history: observable,
            getPreviousPage: action,
            addToHistoryStack: action,
            setListMapValue: action,
            getListMapValue: action,
            setActionButtonMiddle: action,
            setActionButtonLeft: action,
            setActionButtonRight: action,
            resetActionButtons: action,
            setCurrentSubTitle: action,
            setCurrentOwnerId: action,
            setSingleRealEstate: action,
            setIsCondominium: action,
            setIsConsolidation: action,
            setConsolidationId: action,
            setBackbutton: action,
            setTitle: action,
            setTabTitle: action,
            setCustomer: action,
            setUser: action,
            setCloseNavTarget: action,
            setHistory: action,
            navigateTo: action,
            reset: action,
            hasFeatureEasyContact: computed,
            hasFeatureService7000: computed,
            hasFeatureYarowa: computed,
            hasCustomerOptionAppliances: computed,
            hasOptionMaintenances: computed,
            hasOptionRealestatePersons: computed,
            hasOptionTenantBookingsAndFiles: computed,
            hasOptionMultilingual: computed,
            hasOptionCreditors: computed,
            hasOptionUpdatePersonsViaASB: computed,
            hasFeatureTicketing: computed,
            hasFeatureKrediFlow: computed,
            hasFeatureKrediFlowPro: computed,
            hasFeatureKrediFlowAiExtract: computed,
            hasFeatureTenant: computed,
            hasFeatureCondominiumownerportal: computed,
            hasFeatureFlagAssembly: computed,
            hasFeatureFlagAssemblyVoting: computed,
            hasFeatureFlagGhostUser: computed,
            hasFeatureFlagNews: computed
        });
    }

    getPreviousPage = () => {
        if (this.historyStack.length >= 2) {
            return this.historyStack[this.historyStack.length - 2];
        } else {
            return undefined;
        }
    };

    addToHistoryStack = (url: string) => {
        if (this.historyStack.length < HISTORY_STACK_MAX_SIZE) {
            this.historyStack.push(url);
        } else {
            this.historyStack.shift();
            this.historyStack.push(url);
        }
    };

    setListMapValue = (listIdentifier: string, value: ListParameters) => {
        this.listMap.set(listIdentifier, value);
    };

    getListMapValue = (key: string) => {
        return this.listMap.get(key);
    };

    setActionButtonMiddle = (actionButton: React.ReactNode | null) => {
        this.actionButtonMiddle = actionButton;
    };
    setActionButtonLeft = (actionButton: React.ReactNode | null) => {
        this.actionButtonLeft = actionButton;
    };
    setActionButtonRight = (actionButton: React.ReactNode | null) => {
        this.actionButtonRight = actionButton;
    };
    resetActionButtons = () => {
        this.actionButtonRight = null;
        this.actionButtonLeft = null;
        this.actionButtonMiddle = null;
    };

    setCurrentSubTitle = (currentSubTitle: string | null) => {
        this.currentSubtitle = currentSubTitle;
    };

    setCurrentOwnerId = (currentOwnerId: string | null) => {
        this.currentOwnerId = currentOwnerId;
    };

    setSingleRealEstate = (singleRealEstate: boolean) => {
        this.singleRealestate = singleRealEstate;
    };

    setIsCondominium = (isCondominium: boolean) => {
        this.isCondominium = isCondominium;
    };

    setIsConsolidation = (isConsolidation: boolean) => {
        this.isConsolidation = isConsolidation;
    };

    setConsolidationId = (consolidationId: string) => {
        this.consolidationId = consolidationId;
    };

    setBackbutton = (target: string | null, label?: string) => {
        //Remove Backbutton
        if (target === null) {
            this.backButton = null;
            return;
        }
        this.backButton = { label: label || "", to: target };
    };

    setTitle = (newTitle: string) => {
        const titles: string[] = [];
        if (this.customer.name) {
            titles.push(this.customer.name);
        }
        if (newTitle) {
            titles.push(newTitle);
        } else {
            titles.push(Config.appname);
        }
        this.currentTitle = titles.join(" - ");
        this.appheaderTitle = newTitle;
    };

    setTabTitle = (newTitle: string) => {
        this.currentTitle = newTitle;
    };

    setCustomer = (newCustomer: Customer) => {
        this.customer = newCustomer;
    };

    setUser = (newUser: User) => {
        this.user = newUser;
    };

    setCloseNavTarget = (newCloseNavTarget: string) => {
        this.closeNavTarget = newCloseNavTarget;
    };

    setHistory = (history: History) => {
        this.history = history;
    };

    navigateTo = (path: string) => {
        if (this.history) {
            this.history.replace(path);
        } else {
            console.warn("History object not set in NavigationStore");
        }
    };

    reset = () => {
        this.customer = {
            name: "",
            userName: "",
            email: "",
            options: [],
            features: [],
            type: 0,
            featureflags: [],
            erpcoud: false,
            subdomain: ""
        };
        this.closeNavTarget = "";
        this.currentTitle = "";
        this.appheaderTitle = "";
        this.currentSubtitle = null;
        this.singleRealestate = false;
        this.currentOwnerId = null;
        this.backButton = null;
    };

    get hasFeatureEasyContact(): boolean {
        return (
            this.customer.features?.find((feature) => feature.feature === CUSTOMER_FEATURE.EASYCONTACT) !== undefined
        );
    }

    get hasFeatureService7000(): boolean {
        return (
            this.customer.features?.find((feature) => feature.feature === CUSTOMER_FEATURE.SERVICE7000) !== undefined
        );
    }

    get hasFeatureYarowa(): boolean {
        return this.customer.features?.find((feature) => feature.feature === CUSTOMER_FEATURE.YAROWA) !== undefined;
    }

    get hasCustomerOptionAppliances(): boolean {
        return this.customer.options?.find((option) => option.option === CUSTOMER_OPTION.APPLIANCES)?.value === 1;
    }

    get hasOptionMaintenances(): boolean {
        return this.customer.options?.find((option) => option.option === CUSTOMER_OPTION.MAINTENANCES)?.value === 1;
    }

    get hasOptionRealestatePersons(): boolean {
        return (
            this.customer.options?.find((option) => option.option === CUSTOMER_OPTION.REALESTATEPERSONS)?.value === 1
        );
    }

    get hasOptionTenantBookingsAndFiles(): boolean {
        return (
            this.customer.options?.find((option) => option.option === CUSTOMER_OPTION.TENANT_BOOKINGS_AND_FILES)
                ?.value === 1
        );
    }

    get hasOptionMultilingual(): boolean {
        return this.customer.options?.find((option) => option.option === CUSTOMER_OPTION.MULTILINGUAL)?.value === 1;
    }

    get hasOptionCreditors(): boolean {
        return this.customer.options?.find((option) => option.option === CUSTOMER_OPTION.CREDITORS)?.value === 1;
    }

    get hasOptionPaymentPurpose(): boolean {
        return this.customer.options?.find((option) => option.option === CUSTOMER_OPTION.PAYMENT_PURPOSE)?.value === 1;
    }

    // ASB -> Azure Service Bus
    get hasOptionUpdatePersonsViaASB(): boolean {
        return (
            this.customer.options?.find((option) => option.option === CUSTOMER_OPTION.UDPATE_PERSONS_VIA_ASB)?.value ===
            1
        );
    }

    get hasFeatureTicketing(): boolean {
        return this.customer.features?.find((feature) => feature.feature === CUSTOMER_FEATURE.TICKETING) !== undefined;
    }

    get hasFeatureKrediFlow(): boolean {
        return this.customer.features?.find((feature) => feature.feature === CUSTOMER_FEATURE.KREDI_FLOW) !== undefined;
    }

    get hasFeatureKrediFlowPro(): boolean {
        return (
            this.customer.features?.find((feature) => feature.feature === CUSTOMER_FEATURE.KREDI_FLOW_PRO) !== undefined
        );
    }

    get hasFeatureKrediFlowAiExtract(): boolean {
        return (
            this.customer.features?.find((feature) => feature.feature === CUSTOMER_FEATURE.KREDI_FLOW_AI_EXTRACT) !==
            undefined
        );
    }

    get hasFeatureTenant(): boolean {
        return (
            this.customer.features?.find((feature) => feature.feature === CUSTOMER_FEATURE.TENANT_PORTAL) !== undefined
        );
    }

    get hasFeatureCondominiumownerportal(): boolean {
        return (
            this.customer.features?.find((feature) => feature.feature === CUSTOMER_FEATURE.CONDOMINIUMOWNER_PORTAL) !==
            undefined
        );
    }

    get hasFeatureFlagAssembly(): boolean {
        const feature = this.customer.featureflags?.find((f) => f.key === CUSTOMER_FEATURE_FLAGS.ASSEMBLY);
        return feature?.value === "1";
    }

    get hasFeatureFlagAssemblyVoting(): boolean {
        const feature = this.customer.featureflags?.find((f) => f.key === CUSTOMER_FEATURE_FLAGS.ASSEMBLY_VOTING);
        return feature?.value === "1";
    }

    get hasFeatureFlagGhostUser(): boolean {
        const feature = this.customer.featureflags?.find((f) => f.key === CUSTOMER_FEATURE_FLAGS.GHOST_USER);
        return feature?.value === "1";
    }

    get hasFeatureFlagAssemblyFaksimileSignature(): boolean {
        const feature = this.customer.featureflags?.find(
            (f) => f.key === CUSTOMER_FEATURE_FLAGS.ASSEMBLY_FAKSIMILE_SIGNATURE
        );
        return feature?.value === "1";
    }

    get hasFeatureFlagMFA(): boolean {
        const feature = this.customer.featureflags?.find((f) => f.key === CUSTOMER_FEATURE_FLAGS.MFA);
        return feature?.value === "1";
    }

    get hasFeatureFlagNews(): boolean {
        const feature = this.customer.featureflags?.find((f) => f.key === CUSTOMER_FEATURE_FLAGS.NEWS);
        return feature?.value === "1";
    }
}
